/**
 * Bind a skinned equipment piece to the WEARER's skeleton (q-player-armour-visual-fit).
 *
 * TGC-EQP-004's armour contract is skinned binaries authored on the player rig's own
 * skeleton (`player-rig-v1/body-base`, 24 joints). Until 2026-10-23 every armour piece was
 * a rigid placeholder parented to one bone, so the chest shell could not follow the
 * shoulders and the arms passed through it. A skinned piece arrives from the loader with
 * its OWN cloned armature (`cloneTemplate` → `SkeletonUtils.clone`); this re-binds each of
 * its SkinnedMeshes to the wearer's animated bones of the same names, keeping the piece's
 * own inverse bind matrices and bind matrix, then drops the piece's source armature so a
 * later `getObjectByName('Spine')` on the avatar can only find the body's bone.
 *
 * Why the placement of the result does not matter: a SkinnedMesh in three's default
 * `attached` bind mode renders `Σ w · bone.matrixWorld · boneInverse · bindMatrix · v` in
 * world space (bindMatrixInverse tracks the mesh's own matrixWorld), so it follows the
 * wearer's bones wherever it is parented. It is added to the avatar group only for
 * lifetime/disposal.
 *
 * Body-build morphs: a piece may carry shape keys named like the body's (`slight`,
 * `sturdy`); their influences are copied from the body at bind time (the build is set when
 * the rig is built, and a rebuilt rig re-seats equipment through `setPlacement`).
 */

import { Skeleton, type Bone, type Object3D, type SkinnedMesh } from 'three';

/** Find the wearer's bone/node by its runtime (loader-sanitised) name. */
export type WearerBoneLookup = (name: string) => Object3D | null | undefined;

function skinnedMeshesOf(object: Object3D): SkinnedMesh[] {
  const out: SkinnedMesh[] = [];
  object.traverse((node) => {
    if ((node as SkinnedMesh).isSkinnedMesh === true) out.push(node as SkinnedMesh);
  });
  return out;
}

/** The first mesh under `root` carrying morph targets (the body's build morphs), or null. */
export function morphSourceOf(root: Object3D | null | undefined): SkinnedMesh | null {
  let found: SkinnedMesh | null = null;
  root?.traverse((node) => {
    const mesh = node as SkinnedMesh;
    if (found === null && mesh.isSkinnedMesh === true && mesh.morphTargetDictionary !== undefined) {
      found = mesh;
    }
  });
  return found;
}

function copyMorphs(target: SkinnedMesh, source: SkinnedMesh | null): void {
  const dict = target.morphTargetDictionary;
  const influences = target.morphTargetInfluences;
  const from = source?.morphTargetDictionary;
  const fromInfluences = source?.morphTargetInfluences;
  if (!dict || !influences || !from || !fromInfluences) return;
  for (const [name, index] of Object.entries(dict)) {
    const at = from[name];
    if (at !== undefined) influences[index] = fromInfluences[at] ?? 0;
  }
}

/**
 * Re-bind every SkinnedMesh in `object` to the wearer's bones. Returns false — leaving
 * `object` untouched — when it holds no SkinnedMesh (a rigid piece) or any of its joints
 * has no bone of that name on the wearer (an unknown skeleton): the caller then uses the
 * slot's rigid placement.
 */
export function bindSkinnedEquipment(
  object: Object3D,
  wearerBone: WearerBoneLookup,
  morphSource: SkinnedMesh | null = null,
): boolean {
  const meshes = skinnedMeshesOf(object);
  if (meshes.length === 0) return false;
  const plans: Bone[][] = [];
  for (const mesh of meshes) {
    const bones: Bone[] = [];
    for (const joint of mesh.skeleton.bones) {
      const bone = wearerBone(joint.name);
      if (bone === null || bone === undefined) return false;
      bones.push(bone as Bone);
    }
    plans.push(bones);
  }
  meshes.forEach((mesh, i) => {
    const inverses = mesh.skeleton.boneInverses.map((m) => m.clone());
    mesh.bind(new Skeleton(plans[i], inverses), mesh.bindMatrix.clone());
    mesh.frustumCulled = false; // bind-pose bounds; the wearer's animation moves it
    copyMorphs(mesh, morphSource);
  });
  // Keep only the meshes: re-home each under the piece root, then drop everything else
  // (the source armature), so no stray bone named like the body's sits on the avatar.
  for (const mesh of meshes) if (mesh !== object) object.add(mesh);
  for (const child of [...object.children]) {
    if (!meshes.includes(child as SkinnedMesh)) object.remove(child);
  }
  return true;
}
